import { isNativeCapacitorRuntime } from "../services/revenueCatService";
import { pageNavigator } from "./page-navigator";
import { clearLocalStoragePreservingKeys } from "./localStorage";

/*
 * The visitor's session-recording choice (M3, notice plus opt-out, approved
 * 2026-10-19; since 2026-10-28 the choice lives in Settings, "Help improve
 * QLearn", and nothing about recording shows in the study UI).
 *
 * Nothing here loads Hotjar. It answers five questions for replay-document.js
 * and the Settings switch (recording-setting-section.jsx):
 *   - is recording available in this build at all (flag, site id, not the
 *     native app, not the third-party kill switch)?
 *   - does the browser send Global Privacy Control or Do Not Track? (then
 *     Hotjar never loads)
 *   - did this browser turn recording off, or on?
 *   - with no choice stored, is this a visitor whose default is off (a
 *     European or UK time zone)?
 *   - how is it turned off? (delete Hotjar's and Contentsquare's cookies and
 *     storage; a page where the recorder is running also reloads, because a
 *     running recorder cannot be stopped)
 *
 * Opt-out: no stored choice means recording may run, except where the default
 * is off (isDefaultOffRegion). The policy says "we then remember your choice in
 * your browser", so a refusal is kept with no expiry, and anything that makes
 * the stored record unreadable fails towards "off": a refusal with an odd
 * timestamp is still a refusal, and a browser whose local storage cannot be
 * read at all is treated as having refused (the choice could not be remembered
 * across a reload). Turning it on stores "allowed".
 */

export const CHOICE_STORAGE_KEY = "qlearn_session_recording_choice";
export const PURGE_MARKER_KEY = "qlearn_session_recording_purge";
export const CHOICE = Object.freeze({ ALLOWED: "allowed", DECLINED: "declined" });

// Storage can be blocked; the choice then holds for this page only.
let memoryRecord = null;
const listeners = new Set();

const notify = () => {
  listeners.forEach((fn) => {
    try { fn(); } catch (e) { /* a broken subscriber must not break the rest */ }
  });
};

export const subscribeConsent = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// Test seam only.
export const __resetReplayConsent = () => {
  memoryRecord = null;
  recordingReloadInProgress = false;
  beforeReloadHooks.clear();
};

/*
 * Never inside the native app. The iOS build inherits buildspec.yml's flags,
 * so REACT_APP_HOTJAR_ENABLED=true would otherwise reach the App Store build,
 * whose privacy label and own third-party opt-out rule recording out. Either
 * the native Capacitor shell or the build's third-party tracking kill switch
 * (REACT_APP_QLEARN_DISABLE_THIRD_PARTY_TRACKING, read the same way as
 * utils/mixpanel-options.js; the unprefixed spelling is honoured too) blocks
 * it. An error reading either blocks it as well.
 */
export const isRecordingBlockedOnThisRuntime = () => {
  try {
    if (process.env.REACT_APP_QLEARN_DISABLE_THIRD_PARTY_TRACKING === "true") return true;
    if (process.env.REACT_APP_DISABLE_THIRD_PARTY_TRACKING === "true") return true;
    return isNativeCapacitorRuntime();
  } catch (e) {
    return true;
  }
};

// Everything (the start gate, the Settings switch) reads this: off unless the
// flag and site id are built in and the runtime allows it.
export const isReplayFeatureAvailable = () => {
  try {
    if (process.env.REACT_APP_HOTJAR_ENABLED !== "true") return false;
    if (!Number(process.env.REACT_APP_HOTJAR_KEY) || !Number(process.env.REACT_APP_HOTJAR_VERSION)) {
      return false;
    }
    return !isRecordingBlockedOnThisRuntime();
  } catch (e) {
    return false;
  }
};

export const hasGlobalPrivacyControl = () => {
  try {
    return typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
  } catch (e) {
    return false;
  }
};

// Do Not Track, honoured exactly like GPC (2026-10-28). Browsers that still
// send it report "1" on navigator.doNotTrack; older ones on window.doNotTrack.
export const hasDoNotTrack = () => {
  try {
    if (typeof navigator !== "undefined" && navigator.doNotTrack === "1") return true;
    return typeof window !== "undefined" && window.doNotTrack === "1";
  } catch (e) {
    return false;
  }
};

// The browser's own opt-out signal, if any: "gpc", "dnt" or null. Either one
// means recording never runs, whatever is stored; GPC is named first.
export const browserOptOutSignal = () => {
  if (hasGlobalPrivacyControl()) return "gpc";
  if (hasDoNotTrack()) return "dnt";
  return null;
};

export const hasBrowserOptOutSignal = () => browserOptOutSignal() !== null;

const isChoice = (value) => value === CHOICE.ALLOWED || value === CHOICE.DECLINED;

const choiceFrom = (record) => {
  if (!record || typeof record !== "object" || Array.isArray(record)) return null;
  return isChoice(record.choice) ? record.choice : null;
};

const UNREADABLE = Symbol("unreadable");

const storedRecord = () => {
  let raw;
  try {
    raw = window.localStorage.getItem(CHOICE_STORAGE_KEY);
  } catch (e) {
    return UNREADABLE;
  }
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    // A record we wrote is always JSON; a mangled one that still names a
    // refusal is kept as a refusal.
    return typeof raw === "string" && raw.includes(CHOICE.DECLINED) ? { choice: CHOICE.DECLINED } : null;
  }
};

// "declined", "allowed", or null (no choice: recording may run).
export const readChoice = () => {
  const memory = choiceFrom(memoryRecord);
  const stored = storedRecord();
  if (stored === UNREADABLE) return memory || CHOICE.DECLINED;
  const fromStorage = choiceFrom(stored);
  // A refusal in either place wins (another tab may have turned it off, or
  // this page's write may have failed).
  if (fromStorage === CHOICE.DECLINED || memory === CHOICE.DECLINED) return CHOICE.DECLINED;
  return fromStorage || memory;
};

export const writeChoice = (choice, now = Date.now()) => {
  if (!isChoice(choice)) return false;
  const record = { choice, at: now };
  memoryRecord = record;
  try {
    window.localStorage.setItem(CHOICE_STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    // Blocked storage: the in-memory record carries the choice for this page.
  }
  notify();
  return true;
};

// Sign-out clears local storage wholesale; the recording choice is the
// browser's, not the account's, and must survive it ("we then remember your
// choice in your browser"). Every wholesale clear goes through
// clearLocalStoragePreservingKeys with CHOICE_STORAGE_KEY among the kept keys
// (pinned by storage-clear.invariant.test.js); this is the sign-out shape.
export const clearLocalStorageKeepingRecordingChoice = (storage = window.localStorage) => {
  clearLocalStoragePreservingKeys(storage, [CHOICE_STORAGE_KEY]);
};

// ---- the default where nothing is stored ----------------------------------
//
// QLearn's audience is US residents. A visitor whose device is set to a
// European or UK time zone gets recording OFF until they turn it on in
// Settings (EU and UK GDPR expect opt-in for this). The time zone is the only
// signal read: no IP lookup, nothing sent anywhere. It over-covers on purpose
// (every Europe/* zone, including non-EU ones, plus the EU's Atlantic and
// overseas zones and the old aliases browsers still report), because the cost
// of a wrong "off" is one recording we do not get.
const DEFAULT_OFF_TIME_ZONES = new RegExp([
  "^Europe/",
  "^Atlantic/(Azores|Madeira|Canary|Reykjavik|Faroe|Faeroe)$",
  "^Africa/Ceuta$",
  "^Arctic/Longyearbyen$",
  "^Asia/(Nicosia|Famagusta)$",
  "^America/(Guadeloupe|Martinique|Cayenne|Marigot|St_Barthelemy)$",
  "^Indian/(Reunion|Mayotte)$",
  "^(GB|GB-Eire|Eire|Iceland|Portugal|Poland|WET|CET|MET|EET)$",
].join("|"));

export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (e) {
    return "";
  }
};

export const isDefaultOffRegion = (timeZone = browserTimeZone()) => (
  typeof timeZone === "string" && DEFAULT_OFF_TIME_ZONES.test(timeZone)
);

// Off only because of where the visitor is: nothing stored, and a European or
// UK time zone.
export const isOffByRegionDefault = () => readChoice() === null && isDefaultOffRegion();

// The effective choice, for every gate: a stored refusal, or no choice at all
// in a default-off region, means off. A stored "allowed" means on, wherever
// the visitor is (they turned it on in Settings). GPC and Do Not Track are
// checked separately (hasBrowserOptOutSignal).
export const isRecordingOff = () => {
  const choice = readChoice();
  if (choice === CHOICE.DECLINED) return true;
  if (choice === CHOICE.ALLOWED) return false;
  return isDefaultOffRegion();
};

// ---- withdrawal (review B1) ------------------------------------------------

const EXPIRED = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
// Hotjar's loader for this site also runs Contentsquare's tag (Hotjar is part
// of Contentsquare), which writes _cs_* cookies, including a 13-month _cs_id.
// Withdrawal removes both vendors' traces.
// Contentsquare also keeps a pending batch outside those prefixes: the
// localStorage key below and a Cache Storage cache (purged separately), and
// an install-verification marker in session storage (found by the M3
// real-loader check: the only vendor key left after Turn off).
const CONTENTSQUARE_STORAGE_KEYS = ["csAnalysisEventsPersisted", "cs_verify_install_auto"];
const CONTENTSQUARE_CACHE_NAMES = ["csPersisted"];
const isRecorderName = (name) => name.startsWith("_hj") || name.startsWith("_cs_");
const isHotjarStorageKey = (key) => typeof key === "string"
  && (isRecorderName(key) || key === "hjViewportId" || key === "hjActiveViewportIds"
    || CONTENTSQUARE_STORAGE_KEYS.includes(key));

// Hotjar sets its cookies on the top-level domain (.medlearnity.com), so a
// host-only delete is not enough: delete on every parent domain too, never on
// a bare top-level label like ".com".
const parentDomains = (hostname) => {
  if (typeof hostname !== "string" || !hostname || /^[\d.]+$/.test(hostname) || hostname.includes(":")) {
    return [];
  }
  const labels = hostname.split(".").filter(Boolean);
  const out = [];
  for (let i = 0; i < labels.length - 1; i += 1) out.push(`.${labels.slice(i).join(".")}`);
  return out;
};

const purgeStorage = (storage) => {
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) keys.push(storage.key(i));
    keys.filter(isHotjarStorageKey).forEach((key) => storage.removeItem(key));
  } catch (e) {
    // Blocked storage holds nothing of Hotjar's either.
  }
};

export const purgeRecorderTraces = ({
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
} = {}) => {
  let names = [];
  try {
    names = String(document.cookie || "")
      .split(";")
      .map((pair) => pair.split("=")[0].trim())
      .filter(isRecorderName);
  } catch (e) {
    names = [];
  }
  const domains = parentDomains(hostname);
  names.forEach((name) => {
    try {
      document.cookie = `${name}=; ${EXPIRED}; path=/`;
      domains.forEach((domain) => {
        document.cookie = `${name}=; ${EXPIRED}; path=/; domain=${domain}`;
      });
    } catch (e) {
      // Keep going: one failed cookie write must not stop the rest.
    }
  });
  try { purgeStorage(window.localStorage); } catch (e) { /* blocked */ }
  try { purgeStorage(window.sessionStorage); } catch (e) { /* blocked */ }
  // Asynchronous: if the reload wins the race, the purge marker repeats this
  // on the next load.
  try {
    if (typeof window.caches !== "undefined" && window.caches) {
      CONTENTSQUARE_CACHE_NAMES.forEach((name) => {
        window.caches.delete(name).catch(() => {});
      });
    }
  } catch (e) { /* Cache Storage unavailable (insecure context, old browser) */ }
};

// ---- the reload that stops a recording -------------------------------------
//
// It is the app's own reload, so pages that guard against leaving (an active
// quiz) let it through and come back to where the student was (review of
// #866, UX 2): a page registers what to keep with onBeforeRecordingReload, the
// reload is marked in session storage for its address, and the reloaded page
// asks takeRecordingReload() whether this load is that reload.
export const RECORDING_RELOAD_KEY = "qlearn_recording_reload";
const RECORDING_RELOAD_MAX_AGE_MS = 60 * 1000;
const beforeReloadHooks = new Set();
let recordingReloadInProgress = false;

export const onBeforeRecordingReload = (fn) => {
  beforeReloadHooks.add(fn);
  return () => beforeReloadHooks.delete(fn);
};

// True from the moment this page starts its stopping reload: a leave prompt
// (beforeunload) must not stand in its way.
export const isRecordingReloadInProgress = () => recordingReloadInProgress;

const addressKey = (href) => {
  try {
    const u = new URL(href, window.location.href);
    return `${u.pathname.replace(/\/+$/, "") || "/"}${u.search}`;
  } catch (e) {
    return null;
  }
};

// Whether this load is the stopping reload of this same address (and not
// older than a minute). Consumes the mark.
export const takeRecordingReload = (href = window.location.href) => {
  try {
    const raw = window.sessionStorage.getItem(RECORDING_RELOAD_KEY);
    if (!raw) return false;
    window.sessionStorage.removeItem(RECORDING_RELOAD_KEY);
    const mark = JSON.parse(raw);
    return Boolean(mark) && mark.address === addressKey(href)
      && Date.now() - Number(mark.at) < RECORDING_RELOAD_MAX_AGE_MS;
  } catch (e) {
    return false;
  }
};

// A running recorder cannot be stopped; a reload is what stops it. The purge
// marker makes the reloaded page (which never loads Hotjar) purge once more,
// in case Hotjar wrote again while this page unloaded. Used when recording is
// turned off on a page whose recorder is running, and by a recording page
// that hears it was turned off in another tab (replay-document.js).
export const reloadToStopRecording = () => {
  beforeReloadHooks.forEach((fn) => {
    try { fn(); } catch (e) { /* keep going: the reload must happen */ }
  });
  purgeRecorderTraces();
  try {
    window.sessionStorage.setItem(PURGE_MARKER_KEY, "1");
    window.sessionStorage.setItem(RECORDING_RELOAD_KEY, JSON.stringify({ address: addressKey(window.location.href), at: Date.now() }));
  } catch (e) {
    // Without the markers the second purge and the return are skipped; the
    // first purge and the reload stand.
  }
  recordingReloadInProgress = true;
  pageNavigator.reload();
};

// Turning recording off on a page whose recorder is running: store the
// refusal, remove Hotjar's and Contentsquare's cookies and storage, reload.
// (Settings is never recorded, so its switch purges in place instead:
// replay-document.js turnRecordingOff.)
export const withdrawRecording = () => {
  writeChoice(CHOICE.DECLINED);
  reloadToStopRecording();
};

// After the stopping reload: purge once more. Nothing opens or shows: the
// study UI carries no recording notice (owner request, 2026-10-28).
export const runPendingPurge = () => {
  try {
    if (window.sessionStorage.getItem(PURGE_MARKER_KEY) !== "1") return false;
    window.sessionStorage.removeItem(PURGE_MARKER_KEY);
  } catch (e) {
    return false;
  }
  purgeRecorderTraces();
  return true;
};
